'use client';

import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useRouter } from 'next/navigation';
import { Check, Loader2 } from 'lucide-react';
import PageHeader from '@/components/shared/PageHeader';
import GlassPanel from '@/components/dashboard/GlassPanel';
import Button from '@/components/ui/Button';
import { useAuth } from '@/hooks';
import { getSubscriptionPlans } from '@/lib/api/payments';
import type { SubscriptionPlan } from '@/lib/api/payments';
import { cn } from '@/lib/utils';

function getPlanFeatures(plan: SubscriptionPlan): string[] {
  const { features } = plan;
  if (Array.isArray(features)) {
    return features.filter((f): f is string => typeof f === 'string');
  }
  if (features && typeof features === 'object') {
    return Object.entries(features).map(([key, value]) => {
      if (typeof value === 'boolean') return value ? key : `${key} (not included)`;
      return `${key}: ${String(value)}`;
    });
  }
  return [];
}

export default function PricingPage() {
  const { user, isAuthenticated, isLoading: isAuthLoading } = useAuth();
  const router = useRouter();

  const rolePrefix = useMemo(() => {
    if (!user?.role) return null;
    return user.role === 'employer' ? 'employer_' : 'seafarer_';
  }, [user?.role]);

  const { data, isLoading: isPlansLoading } = useQuery({
    queryKey: ['subscription-plans', rolePrefix],
    queryFn: () => getSubscriptionPlans({ ordering: 'display_order' }),
    enabled: isAuthenticated,
  });

  const plans = useMemo(() => {
    const all = data?.results ?? [];
    if (!rolePrefix) return all;
    return all.filter((plan) => plan.planType.startsWith(rolePrefix));
  }, [data, rolePrefix]);

  const isLoading = isAuthLoading || isPlansLoading;

  const handleChoosePlan = (planId: number) => {
    if (!isAuthenticated) {
      router.push(`/login?redirect=/billing?checkout=${planId}`);
      return;
    }
    router.push(`/billing?checkout=${planId}`);
  };

  return (
    <div className="min-h-screen bg-obsidian py-12">
      <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
        <PageHeader
          title="Choose your plan"
          subtitle="Transparent pricing for maritime recruitment and career tools."
        />

        {!isAuthenticated && (
          <GlassPanel className="mt-8 text-center">
            <p className="text-foreground">
              Sign in to view plans and start your subscription.
            </p>
            <Button className="mt-4" onClick={() => router.push('/login')}>
              Sign in
            </Button>
          </GlassPanel>
        )}

        {isLoading && (
          <div className="mt-12 flex items-center justify-center">
            <Loader2 className="h-8 w-8 animate-spin text-neon-cyan" />
          </div>
        )}

        {!isLoading && isAuthenticated && plans.length === 0 && (
          <GlassPanel className="mt-8 text-center">
            <p className="text-ash">No plans are available for your account type right now.</p>
          </GlassPanel>
        )}

        {plans.length > 0 && (
          <div className="mt-10 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {plans.map((plan) => {
              const features = getPlanFeatures(plan);
              return (
                <GlassPanel
                  key={plan.id}
                  className={cn(
                    'flex flex-col',
                    plan.planType.includes('pro') || plan.planType.includes('enterprise')
                      ? 'border-neon-cyan/30'
                      : 'border-neon-cyan/10'
                  )}
                >
                  <div className="border-b border-neon-cyan/10 pb-4">
                    <h3 className="text-lg font-semibold text-foreground">{plan.name}</h3>
                    <p className="mt-1 text-sm text-ash">{plan.description}</p>
                  </div>

                  <div className="py-6">
                    <div className="flex items-baseline gap-1">
                      <span className="text-3xl font-bold text-foreground">
                        {plan.priceDisplay.currency} {plan.priceDisplay.amount}
                      </span>
                      <span className="text-sm text-ash">/{plan.billingCycle}</span>
                    </div>
                  </div>

                  {features.length > 0 && (
                    <ul className="mb-6 flex-1 space-y-2">
                      {features.map((feature, idx) => (
                        <li key={idx} className="flex items-start gap-2 text-sm text-ash">
                          <Check className="mt-0.5 h-4 w-4 flex-shrink-0 text-neon-green" />
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>
                  )}

                  <Button
                    className="w-full"
                    onClick={() => handleChoosePlan(plan.id)}
                  >
                    Choose plan
                  </Button>
                </GlassPanel>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
